import { DefaultDateRangePickerInput } from "../../../../../../common/components/default_date_range_picker_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { useMemo } from "react";
import { MaskedInput } from "../../../../../../common/components/masked_inputs/masked_input";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";


/**
 * Header
 */

type Props = {
  nroRequisicao: number | null;
  dataRequisicaoStart: Date | null;
  dataRequisicaoEnd: Date | null;
  centroCustos: number[] | null;
  solicitantes: number[] | null;
  status: number | null;
  produtosServicos: number[] | null;
  dataPrevistaStart: Date | null;
  dataPrevistaEnd: Date | null;
  centroCustoDropdown: CentroCustoDropDown[];
  funcionarioDropdown: FuncionarioDropDown[];
  produtosServicosDropdown: ProdutoServicoDropDown[];
  onNroRequisicaoChange: (nroRequisicao: number | null) => void;
  onDataRequisicaoChange: (de: Date | null, ate: Date | null) => void;
  onCentroCustosChange: (centroCustos: number[] | null) => void;
  onSolicitantesChange: (solicitantes: number[] | null) => void;
  onStatusChange: (status: number | null) => void;
  onProdutosServicosChange: (produtosServicos: number[] | null) => void;
  onDataPrevistaChange: (de: Date | null, ate: Date | null) => void;
  onClearFilter: () => void;
};

export const Header = ({
  nroRequisicao,
  dataRequisicaoStart,
  dataRequisicaoEnd,
  centroCustos,
  solicitantes,
  status,
  produtosServicos,
  dataPrevistaStart,
  dataPrevistaEnd,
  centroCustoDropdown,
  funcionarioDropdown,
  produtosServicosDropdown,
  onNroRequisicaoChange,
  onDataRequisicaoChange,
  onCentroCustosChange,
  onSolicitantesChange,
  onStatusChange,
  onProdutosServicosChange,
  onDataPrevistaChange,
  onClearFilter,
}: Props) => {

  const selectedCentroCustos = useMemo(() => {
    if (!centroCustos || !centroCustoDropdown) return [];
    if (centroCustos.length === 0 || centroCustoDropdown.length === 0) return [];
    return centroCustoDropdown.filter(x => centroCustos.some(y => y === x.id));
  }, [
    centroCustos,
    centroCustoDropdown,
  ]);

  const selectedFuncionarios = useMemo(() => {
    if (!solicitantes || !funcionarioDropdown) return [];
    if (solicitantes.length === 0 || funcionarioDropdown.length === 0) return [];
    return funcionarioDropdown.filter(x => solicitantes.some(y => y === x.idFuncionario));
  }, [
    solicitantes,
    funcionarioDropdown,
  ]);

  const selectedProdutosServicos = useMemo(() => {
    if (!produtosServicos || !produtosServicosDropdown) return [];
    if (produtosServicos.length === 0 || produtosServicosDropdown.length === 0) return [];
    return produtosServicosDropdown.filter(x => produtosServicos.some(y => y === x.idProdutoServico));
  }, [
    produtosServicos,
    produtosServicosDropdown,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Todas as requisições',
      },
      {
        id: 2,
        label: 'Pendentes',
      },
      {
        id: 3,
        label: 'Aprovadas',
      },
      {
        id: 4,
        label: 'Reprovadas',
      },
      {
        id: 5,
        label: 'Finalizadas',
      },
      {
        id: 6,
        label: 'Atrasadas',
      },
      {
        id: 7,
        label: 'Coleta Iniciada',
      },
      {
        id: 8,
        label: 'Revisar',
      },
    ];
  }, [
  ]);

  return (
    <CascateList title='Filtros' defaultOpen={true}>
      <div className="w-full flex flex-col gap-2">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-x-2 gap-y-4">
          <MaskedInput
            name='nroRequisicao'
            label='Nro da Requisição'
            mask='000000000'
            maskPlaceholder='_________'
            value={nroRequisicao?.toString() ?? null}
            onChange={e => onNroRequisicaoChange(e && e.length > 0 ? Number(e) : null)}
          />

          <DefaultDateRangePickerInput
            label='Data da Requisição'
            dataDe={dataRequisicaoStart}
            dataAte={dataRequisicaoEnd}
            onChange={(de, ate) => onDataRequisicaoChange(de, ate)}
          />

          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustoDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCustos}
            onChange={e => onCentroCustosChange(e && e.length > 0 ? e.map(x => x.id) : null)}
          />

          <DefaultMultiAutocomplete
            label='Solicitante'
            options={funcionarioDropdown}
            getOptionKey={x => x.idFuncionario}
            getOptionLabel={x => x.nome}
            value={selectedFuncionarios}
            onChange={e => onSolicitantesChange(e && e.length > 0 ? e.map(x => x.idFuncionario) : null)}
          />

          <DefaultSelectInput
            name='status'
            label='Status'
            showSelectionLabel={false}
            options={statusOptions}
            value={status}
            onChangeValue={e => onStatusChange(e ? Number(e) : null)}
          />

          <DefaultMultiAutocomplete
            label='Produtos/Serviços'
            options={produtosServicosDropdown}
            getOptionKey={x => x.idProdutoServico}
            getOptionLabel={x => x.descricao}
            value={selectedProdutosServicos}
            onChange={e => onProdutosServicosChange(e && e.length > 0 ? e.map(x => x.idProdutoServico) : null)}
          />

          <DefaultDateRangePickerInput
            label='Data da Prevista'
            dataDe={dataPrevistaStart}
            dataAte={dataPrevistaEnd}
            onChange={(de, ate) => onDataPrevistaChange(de, ate)}
          />
        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={() => onClearFilter()}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}